/**
 * Peças pequenas da página do setor: avatar com anel de estado, seletor
 * segmentado, erro de ação (com Recarregar no 409) e o hook que o alimenta.
 */
import React, { useCallback, useState } from "react";

import { ehConflito } from "../../../services/mapaProducao";
import { iniciais } from "./modelo";
import { Avatar, ErroInline, Pill, Segmentado, Segmento } from "./styles";

/** Avatar de iniciais. `tom` = o que a pessoa faz (info trabalhando, warning pausada). */
export function AvatarPessoa({ nome, tom = "neutral", trabalhando = false, tamanho = 32, title }) {
  return (
    <Avatar
      $tom={tom}
      $tamanho={tamanho}
      data-trabalhando={trabalhando ? "sim" : undefined}
      title={title || nome}
      aria-hidden="true"
    >
      {iniciais(nome)}
    </Avatar>
  );
}

export function PillStatus({ tom = "neutral", children, ponto = true, title }) {
  return (
    <Pill $tom={tom} data-ponto={ponto ? "sim" : undefined} title={title}>
      {children}
    </Pill>
  );
}

/**
 * Seletor segmentado acessível (radiogroup). `opcoes`: [{valor, rotulo,
 * icone?, n?}]. `compacto`: em telas médias fica só o ícone (o rótulo vira
 * o nome acessível).
 */
export function SeletorSegmentado({ rotulo, opcoes, valor, onChange, compacto = false }) {
  return (
    <Segmentado role="radiogroup" aria-label={rotulo}>
      {opcoes.map((opcao) => {
        const Icone = opcao.icone;
        const ativo = opcao.valor === valor;
        return (
          <Segmento
            key={opcao.valor}
            type="button"
            role="radio"
            aria-checked={ativo}
            aria-label={opcao.n != null ? `${opcao.rotulo} (${opcao.n})` : opcao.rotulo}
            title={opcao.rotulo}
            $ativo={ativo}
            data-compacto={compacto ? "sim" : undefined}
            onClick={() => onChange(opcao.valor)}
          >
            {Icone && <Icone size={14} aria-hidden="true" />}
            <span className="texto">{opcao.rotulo}</span>
            {opcao.n != null && <span className="n">{opcao.n}</span>}
          </Segmento>
        );
      })}
    </Segmentado>
  );
}

/**
 * Estado de erro de uma ação de painel. `rodar(fn)` limpa o erro, executa e,
 * se falhar, guarda a mensagem do backend (e se foi conflito) e relança —
 * o AsyncButton com `silentError` para o spinner sem toast duplicado.
 */
export function useErroDeAcao() {
  const [erro, setErro] = useState(null);
  const rodar = useCallback(async (fn) => {
    setErro(null);
    try {
      return await fn();
    } catch (e) {
      setErro({ mensagem: e?.message || "Não foi possível concluir.", conflito: ehConflito(e) });
      throw e;
    }
  }, []);
  const limpar = useCallback(() => setErro(null), []);
  return { erro, rodar, limpar };
}

export function ErroDaAcao({ erro, onRecarregar }) {
  if (!erro) return null;
  return (
    <ErroInline role="alert">
      <span>{erro.mensagem}</span>
      {erro.conflito && onRecarregar && (
        <button type="button" className="btn btn--secondary btn--sm" onClick={onRecarregar}>
          Recarregar
        </button>
      )}
    </ErroInline>
  );
}
